import { Mesh, Vector3 } from '@babylonjs/core';
import { Entity } from './Entity';
import { GameConfig } from './GameConfig';
import { Level } from './Level';
import { AssetObjectConfig, MeshFactory } from './MeshFactory';

export class Checkpoint implements Entity {
  public active: boolean = false;

  constructor(
    public mesh: Mesh,
    public pathIndex: number,
    public config: any
  ) {}
}

export class CheckpointGenerator {
  checkpoints: Checkpoint[] = [];
  lastActivatedCheckpoint: Checkpoint | null = null;

  constructor(private level: Level) {}

  generate(): void {
    const interval = this.level.config.checkpointInterval;

    if (!interval || interval <= 0) {
      return;
    }

    const pathPoints = this.level.pathPoints;

    // Start after first interval, stop before end
    for (let i = interval; i < pathPoints.length - interval; i += interval) {
      this.createCheckpoint(i);
    }

    if (GameConfig.logDebug) {
      console.log(`[CheckpointGenerator] Created ${this.checkpoints.length} checkpoints`);
    }
  }

  private createCheckpoint(pathIndex: number) {
    if (this.level.occupiedPathIndexes.includes(pathIndex)) {
      return;
    }

    if (this.level.config.platform && !this.level.config.way) {
      const platform = this.level.getPlatformByPathIndex(pathIndex);
      if (!platform) {
        return;
      }
      if (platform.config.moving || platform.config.crumbling) {
        // no instance on moving platforms, too stressful
        return;
      }
    }

    // Find asset object
    const assetObject = this.level.assetObjects.find((a) => a.type === 'checkpoint');

    const config = AssetObjectConfig.builder()
      .object(assetObject)
      .scale(2)
      .physicsShapeType(undefined) // No physics
      .build();

    const mesh = config.buildMesh(this.level) as Mesh;
    MeshFactory.configureMesh(mesh, config, this.level);

    let position = this.level.getMaxPathPosition(pathIndex);

    mesh.position.copyFrom(position);

    // Set initial visibility (alpha 0.4)
    mesh.visibility = 0.4;
    mesh.checkCollisions = false;

    // Rotate to face path direction
    // Look at next point
    if (!this.level.config.platform) {
      if (pathIndex < this.level.pathPoints.length - 1) {
        mesh.lookAt(this.level.pathPoints[pathIndex + 1]);
      }
    }
    const checkpoint = new Checkpoint(mesh, pathIndex, config);
    this.checkpoints.push(checkpoint);
    this.level.occupiedPathIndexes.push(pathIndex - 1);
    this.level.occupiedPathIndexes.push(pathIndex);
    this.level.occupiedPathIndexes.push(pathIndex + 1);
  }

  update() {
    // Check collision with player
    const playerPos = this.level.player.model.position;

    // Optimization: Check distance to all checkpoints?
    // Since we usually have < 100 checkpoints, a simple loop is fine.
    // We only care about inactive ones for activation.
    // However, we might want to know if we crossed a new one.

    // Simple radius check
    const activationRadius = 2.0;

    for (const cp of this.checkpoints) {
      if (cp.active) continue;

      if (Vector3.Distance(cp.mesh.position, playerPos) < activationRadius) {
        this.activateCheckpoint(cp);
      }
    }
  }

  activateCheckpoint(cp: Checkpoint) {
    if (cp.active) return;

    cp.active = true;
    cp.mesh.visibility = 0.8;
    this.lastActivatedCheckpoint = cp;
    this.level.player.score += 100;
    this.level.hud.showMessage('Checkpoint', 1500, true);
    this.level.game.sound.textToSpeech('Checkpoint');
    this.level.game.sound.play('checkpoint');

    // Beautiful particle effect on checkpoint activation
    this.level.particles.createCheckpointActivationEffect(cp.mesh.position.clone());
  }

  getLastActivatedCheckpointPosition(): Vector3 | null {
    if (this.lastActivatedCheckpoint) {
      // Return a clone of the position + slight offset if needed (e.g. up a bit)
      // Usually safe to spawn exactly there if it's on the path + y offset
      return this.lastActivatedCheckpoint.mesh.position.clone();
    }
    return null;
  }

  dispose() {
    for (const cp of this.checkpoints) {
      cp.mesh.dispose();
    }
    this.checkpoints = [];
    this.lastActivatedCheckpoint = null;
  }
}
